/**
 * Serialize AOM archetypes/templates to ADL 1.4 text (down-convert from internal ADL2 model).
 */

import * as openehr_am from "../am/openehr_am.ts";
import { ADL2Serializer } from "./adl2_serializer.ts";
import {
  expandAcConstraintsInAdl,
  idToAtCode,
  rewriteAdl2CodesInTextWithMap,
  type Adl2TermTable,
  rewriteOntologyTermKeys,
  stripValueSetsBlock,
  type ValueSetBag,
} from "./id_to_at_code.ts";
import {
  bindingKeyKind,
  formatCodePhrase,
  isBindingUri,
  parseTermBinding,
} from "./term_binding.ts";

export interface Adl14SerializerConfig {
  indent?: string;
  rmRelease?: string;
}

export class ADL14Serializer {
  private config: Required<Adl14SerializerConfig>;
  private inner: ADL2Serializer;
  /** Warnings from the last {@link serialize} call (e.g. renumbered node ids, #127). */
  warnings: string[] = [];

  constructor(config?: Adl14SerializerConfig) {
    this.config = {
      indent: config?.indent ?? "    ",
      rmRelease: config?.rmRelease ?? "1.0.4",
    };
    this.inner = new ADL2Serializer({ indent: this.config.indent });
  }

  serialize(archetype: openehr_am.ARCHETYPE): string {
    this.warnings = [];
    this.assertSerializableDefinition(archetype);
    let adl = this.inner.serialize(archetype);
    adl = this.toAdl14Header(adl, archetype);
    adl = this.insertConceptSection(adl, archetype);
    adl = adl.replace(/\bterminology\b/g, "ontology");
    // One collision-free id/at → 1.4 map for the whole archetype (spec rule:
    // id1 → at0000; value codes renumbered if they would clash with a node).
    const rewritten = rewriteAdl2CodesInTextWithMap(adl, {
      terms: (archetype.ontology as { term_definitions?: Adl2TermTable } | undefined)
        ?.term_definitions,
      bindings: (archetype.ontology as
        | { term_bindings?: Record<string, Record<string, string>> }
        | undefined)?.term_bindings,
      warn: (m) => this.warnings.push(m),
    });
    adl = rewritten.adl;
    adl = this.convertNodeIdsToAtCodes(adl);
    adl = rewriteOntologyTermKeys(adl);
    const valueSets = (archetype.ontology as { value_sets?: ValueSetBag } | undefined)
      ?.value_sets;
    const expanded = expandAcConstraintsInAdl(
      adl,
      valueSets,
      (c) => {
        const m = rewritten.map.get(c);
        return m && !m.includes("::") ? m : idToAtCode(c);
      },
    );
    adl = expanded.adl;
    adl = stripValueSetsBlock(adl);
    adl = this.wrapTermDefinitionItems(adl);
    adl = this.insertTermBindings(adl, archetype, rewritten.map);
    return adl.trimEnd() + "\n";
  }

  private toAdl14Header(adl: string, _archetype: openehr_am.ARCHETYPE): string {
    return adl.replace(
      /^archetype\s*\([^)]*\)/m,
      `archetype (adl_version=1.4; rm_release=${this.config.rmRelease}; generated)`,
    ).replace(
      /(\s+)(openEHR[^\s]+)\.v(\d+)\.(\d+)\.(\d+)/g,
      (_m, indent: string, base: string, maj: string) =>
        `${indent}${base}.v${maj}`,
    );
  }

  /**
   * Insert ADL 1.4 `concept` section after archetype id / specialize (#115).
   */
  private insertConceptSection(
    adl: string,
    archetype: openehr_am.ARCHETYPE,
  ): string {
    if (/^concept\b/m.test(adl)) return adl;
    const conceptCode = this.conceptAtCode(archetype);
    const conceptText = this.conceptText(archetype, conceptCode);
    const block =
      `concept\n    [${conceptCode}]    -- ${
        conceptText.replace(/\n/g, " ")
      }\n\n`;

    const parts = adl.split("\n");
    let insertAt = -1;
    for (let i = 0; i < parts.length; i++) {
      if (/^specialize\b/.test(parts[i])) {
        // insert after specialize + parent id + blank line
        insertAt = i + 1;
        while (insertAt < parts.length && parts[insertAt].trim() !== "") {
          insertAt++;
        }
        insertAt++; // after blank line
        break;
      }
    }
    if (insertAt < 0) {
      // after archetype header + id + blank
      for (let i = 0; i < parts.length; i++) {
        if (/^archetype\b/.test(parts[i])) {
          insertAt = i + 1;
          while (insertAt < parts.length && parts[insertAt].trim() !== "") {
            insertAt++;
          }
          insertAt++;
          break;
        }
      }
    }
    if (insertAt < 0) insertAt = 0;
    parts.splice(insertAt, 0, ...block.trimEnd().split("\n"), "");
    return parts.join("\n");
  }

  private conceptAtCode(archetype: openehr_am.ARCHETYPE): string {
    if (archetype.concept) {
      return this.idToAtCode(String(archetype.concept));
    }
    const nodeId = archetype.definition?.node_id;
    if (nodeId) return this.idToAtCode(nodeId);
    return "at0000";
  }

  private conceptText(
    archetype: openehr_am.ARCHETYPE,
    conceptCode: string,
  ): string {
    const ontology = archetype.ontology as {
      term_definitions?: Record<
        string,
        Record<string, { text?: string }>
      >;
    } | undefined;
    const defs = ontology?.term_definitions;
    if (!defs) return conceptCode;
    const nodeId = archetype.definition?.node_id;
    for (const lang of ["en", ...Object.keys(defs)]) {
      const table = defs[lang];
      if (!table) continue;
      for (const key of [conceptCode, nodeId, this.idToAtCode(nodeId ?? "")]) {
        if (key && table[key]?.text) return table[key].text!;
      }
      // ADL2 term keys are still idN before ontology rewrite
      if (nodeId && table[nodeId]?.text) return table[nodeId].text!;
    }
    return conceptCode;
  }

  private idToAtCode(code: string): string {
    return idToAtCode(code) || "at0000";
  }

  /**
   * Refuse to emit invalid ADL 1.4 when the AOM definition is missing or empty.
   * (@see https://github.com/ErikSundvall/ehrtslib/issues/113)
   */
  private assertSerializableDefinition(archetype: openehr_am.ARCHETYPE): void {
    const def = archetype.definition;
    if (!def || !def.rm_type_name || !def.node_id) {
      throw new Error(
        "ADL14Serializer: refuse to serialize archetype with empty or incomplete definition " +
          "(missing rm_type_name/node_id). This usually means cADL parsing failed; " +
          "see parse result errors / issue #113.",
      );
    }
  }

  private convertNodeIdsToAtCodes(adl: string): string {
    const lines = adl.split("\n");
    let inDefinition = false;
    let inRules = false;
    const out: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (/^definition\b/i.test(trimmed)) {
        inDefinition = true;
        inRules = false;
      } else if (/^rules\b/i.test(trimmed)) {
        inRules = true;
        inDefinition = false;
      } else if (
        /^(ontology|terminology|language|description|annotations|concept|specialize|specialise)\b/i
          .test(trimmed)
      ) {
        inDefinition = false;
        inRules = false;
      }

      if (inDefinition || inRules) {
        out.push(
          line.replace(/\[id(\d+(?:\.\d+)*)\]/gi, (_m, n: string) => {
            return `[${this.idToAtCode("id" + n)}]`;
          }),
        );
      } else {
        out.push(line);
      }
    }
    return out.join("\n");
  }

  /**
   * ADL 1.4 ontology: ["en"] = < items = < … > >.
   * Port of Archie's line-based wrapTermDefinitionItems.
   */
  private wrapTermDefinitionItems(adl: string): string {
    const td = adl.indexOf("term_definitions");
    if (td < 0) return adl;
    if (adl.slice(td).includes("items = <")) return adl;

    const head = adl.slice(0, td);
    const rest = adl.slice(td);
    const lines = rest.split("\n");
    const out: string[] = [];
    let inTermDefs = false;
    let langIndent: string | null = null;
    let openedItems = false;

    for (const line of lines) {
      if (/^\s*term_definitions\s*=\s*<\s*$/.test(line)) {
        inTermDefs = true;
        out.push(line);
        continue;
      }
      if (inTermDefs) {
        const lm = /^([ \t]*)\["([^"]+)"\]\s*=\s*<\s*$/.exec(line);
        if (lm && !openedItems) {
          langIndent = lm[1];
          out.push(line);
          out.push(`${langIndent}    items = <`);
          openedItems = true;
          continue;
        }
        if (openedItems && langIndent !== null) {
          if (line === `${langIndent}>` || line === `${langIndent}>\r`) {
            out.push(`${langIndent}    >`);
            out.push(line);
            openedItems = false;
            langIndent = null;
            continue;
          }
        }
        if (/^\s*>\s*$/.test(line) && !openedItems) {
          inTermDefs = false;
        }
      }
      out.push(line);
    }
    return head + out.join("\n");
  }

  /**
   * ADL2 `term_bindings` are not written by the ADL2 serializer. Emit ADL 1.4
   * `term_bindings` (at/id codes and paths) and `constraint_bindings` (ac codes)
   * from the archetype ontology (#141). Unmapped targets are skipped with a warning.
   */
  private insertTermBindings(
    adl: string,
    archetype: openehr_am.ARCHETYPE,
    codeMap: Map<string, string>,
  ): string {
    const block = this.formatBindingBlocks(archetype, codeMap);
    if (!block) return adl;
    const ontologyAt = adl.search(/^ontology\b/m);
    const next = /^(annotations|rm_overlay)\b/m.exec(adl);
    if (ontologyAt >= 0 && next && next.index > ontologyAt) {
      return adl.slice(0, next.index) + block + "\n" + adl.slice(next.index);
    }
    if (ontologyAt >= 0) {
      return adl.trimEnd() + "\n" + block + "\n";
    }
    return adl.trimEnd() + "\n\nontology\n" + block + "\n";
  }

  private formatBindingBlocks(
    archetype: openehr_am.ARCHETYPE,
    codeMap: Map<string, string>,
  ): string {
    const raw = (archetype.ontology as {
      term_bindings?: Record<string, Record<string, unknown>>;
    } | undefined)?.term_bindings;
    if (!raw) return "";
    const terms = new Map<string, Array<{ key: string; value: string }>>();
    const constraints = new Map<string, Array<{ key: string; value: string }>>();

    for (const [termKey, bag] of Object.entries(raw)) {
      if (!bag || typeof bag !== "object") continue;
      for (const [code, value] of Object.entries(bag)) {
        if (typeof value !== "string") {
          this.warnings.push(
            `Cannot export term binding ${termKey}[${code}]: target is not a string`,
          );
          continue;
        }
        const kind = bindingKeyKind(code);
        // Written back as the external code itself (#252): no binding needed.
        if (codeMap.get(code)?.includes("::")) continue;
        const mappedKey = this.mapBindingKey(code, codeMap);
        if (!mappedKey) {
          this.warnings.push(
            `Cannot export term binding ${termKey}[${code}]: code was dropped in ADL 1.4`,
          );
          continue;
        }
        const parsed = parseTermBinding(termKey, value);
        const phrase = parsed.binding
          ? formatCodePhrase(parsed.binding.terminology, parsed.binding.code)
          : undefined;

        if (kind === "constraint") {
          const emitted = phrase ??
            (isBindingUri(value) ? value.trim() : undefined);
          if (!emitted) {
            this.warnings.push(
              `Cannot export term binding ${termKey}[${code}]: ${
                parsed.warning ?? "unrecognized target"
              }`,
            );
            continue;
          }
          pushBinding(constraints, termKey, mappedKey, emitted);
          continue;
        }

        if (kind !== "code" && kind !== "path") {
          this.warnings.push(
            `Cannot export term binding ${termKey}[${code}]: unsupported key`,
          );
          continue;
        }
        if (phrase) {
          pushBinding(terms, termKey, mappedKey, phrase);
          continue;
        }
        if (kind === "path" && isBindingUri(value)) {
          pushBinding(terms, termKey, mappedKey, value.trim());
          continue;
        }
        this.warnings.push(
          `Cannot export term binding ${termKey}[${code}]: ${
            parsed.warning ?? "unrecognized target"
          }`,
        );
      }
    }

    return [
      formatBindingSection("term_bindings", terms),
      formatBindingSection("constraint_bindings", constraints),
    ].filter(Boolean).join("\n");
  }

  private mapBindingKey(
    code: string,
    codeMap: Map<string, string>,
  ): string | undefined {
    if (code.startsWith("/")) {
      let dropped = false;
      const path = code.replace(
        /\[((?:id|at)\d+(?:\.\d+)*)\]/gi,
        (_m, c: string) => {
          const mapped = mappedCode(c, codeMap);
          if (!mapped) {
            dropped = true;
            return "[]";
          }
          return `[${mapped}]`;
        },
      );
      return dropped ? undefined : path;
    }
    return mappedCode(code, codeMap);
  }

}

function mappedCode(
  code: string,
  codeMap: Map<string, string>,
): string | undefined {
  if (codeMap.has(code)) {
    const v = codeMap.get(code) ?? "";
    return v === "" ? undefined : v;
  }
  const shifted = idToAtCode(code);
  return shifted === "" ? undefined : shifted;
}

function pushBinding(
  groups: Map<string, Array<{ key: string; value: string }>>,
  terminology: string,
  key: string,
  value: string,
): void {
  const list = groups.get(terminology) ?? [];
  list.push({ key, value });
  groups.set(terminology, list);
}

function odinKey(key: string): string {
  return `["${key.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
}

function formatBindingSection(
  name: "term_bindings" | "constraint_bindings",
  groups: Map<string, Array<{ key: string; value: string }>>,
): string {
  if (groups.size === 0) return "";
  const lines = [`    ${name} = <`];
  for (const term of [...groups.keys()].sort()) {
    lines.push(`        ${odinKey(term)} = <`);
    lines.push(`            items = <`);
    const items = groups.get(term) ?? [];
    items.sort((a, b) => a.key.localeCompare(b.key));
    for (const item of items) {
      lines.push(`                ${odinKey(item.key)} = <${item.value}>`);
    }
    lines.push(`            >`);
    lines.push(`        >`);
  }
  lines.push(`    >`);
  return lines.join("\n");
}

/** Round-trip metrics for tests. */
export function adl14RoundTripMetrics(
  original: string,
  reserialized: string,
): { lineCountDelta: number; nodeIdAtCodes: number } {
  const atCount = (reserialized.match(/\[at\d+(?:\.\d+)*\]/g) ?? []).length;
  const origLines = original.split("\n").length;
  const newLines = reserialized.split("\n").length;
  return { lineCountDelta: newLines - origLines, nodeIdAtCodes: atCount };
}
